import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import { Tooltip } from '@neo/primitives/tooltip'
import { Info } from 'lucide-react'
import { useStartNodeType } from 'v2/pages/flow/hooks/use-start-node-type'
import { FlowTypes } from 'v2/types'
import type { QNRFieldRendererProps } from './types'

export const RoutingStrategy = ({ validations, form }: QNRFieldRendererProps) => {
  const type = useStartNodeType()
  return (
    <FieldsRenderer
      fields={[
        {
          name: 'routing',
          label: 'Routing Strategy',
          componentType: 'select',
          zodValidation: validations.shape.routing,
          inputProps: {
            options: [
              { value: 'Availability', label: 'Highest Idle Time' },
              { value: 'Simultaneous', label: 'Simultaneous' },
              {
                value: 'Lowest Conversations Handled',
                label: 'Lowest Conversations Handled',
              },
              {
                value: 'Lowest Conversation Handle Time',
                label: 'Lowest Conversation Handle Time',
              },
            ],
          },
          afterOnChange: (values, _, formInstance) => {
            if (values.routing === 'Simultaneous') {
              formInstance?.setFieldValue('retry_limit', 0)
              formInstance?.validateField('retry_limit', 'change')
            }
          },
          className: 'mb-4',
        },
        {
          name: 'auto_accept_call.allow_auto_accept',
          componentType: 'switch',
          label: 'Auto accept calls by humans',
          zodValidation: validations.shape.auto_accept_call.shape.allow_auto_accept,
          visible: () => type === FlowTypes.incoming_call,
          afterOnChange: (values, _, formInstance) => {
            if (values.auto_accept_call.allow_auto_accept) {
              formInstance?.setFieldValue('agent_limit', 1)
              formInstance?.validateField('agent_limit', 'change')
            }
          },
          className: 'mb-4',
        },
        {
          name: 'auto_accept_call.auto_accept_delay',
          componentType: 'inputNumber',
          onChangeListenTo: ['auto_accept_call.allow_auto_accept'],
          label: (
            <div className="flex items-center gap-1">
              Ring time before auto answering calls{' '}
              <Tooltip content="A delay can be set before an incoming call is auto-answered">
                <Info className="h-2.5 w-2.5 text-muted-foreground" />
              </Tooltip>
            </div>
          ),
          zodValidation: validations.shape.auto_accept_call.shape.auto_accept_delay,
          getDynamicProps: (values) => ({
            inputProps: {
              type: 'number',
              endIcon: <div className="text-p-400 mr-7 text-secondary">seconds</div>,
              disabled: !values.auto_accept_call.allow_auto_accept,
            },
          }),
          visible: () => type === FlowTypes.incoming_call,
          className: 'mb-4',
        },
        {
          name: 'agent_limit',
          componentType: 'inputNumber',
          label: (
            <div className="flex items-center gap-1">
              Simultaneous conversation recipients
              {type === FlowTypes.incoming_call && (
                <Tooltip content="Calls will be routed to a single agent if auto-answer is turned on.">
                  <Info className="h-2.5 w-2.5 text-muted-foreground" />
                </Tooltip>
              )}
            </div>
          ),
          zodValidation: validations.shape.agent_limit,
          onChangeListenTo: ['auto_accept_call.allow_auto_accept'],
          getDynamicProps: (values) => ({
            inputProps: {
              type: 'number',
              endIcon: <div className="text-p-400 mr-7 text-secondary">agents</div>,
              disabled: values.auto_accept_call.allow_auto_accept,
            },
          }),
          className: 'mb-4',
        },
        {
          name: 'retry_limit',
          componentType: 'inputNumber',
          label: 'Conversation Assignment Retry',
          zodValidation: validations.shape.retry_limit,
          onChangeListenTo: ['routing'],
          getDynamicProps: (values) => ({
            inputProps: {
              type: 'number',
              max: 3,
              min: 0,
              endIcon: <div className="text-p-400 mr-7 text-secondary">times</div>,
              disabled: values.routing === 'Simultaneous',
            },
          }),
          className: 'mb-4',
        },
        {
          name: 'sticky_routing_options',
          label: 'Sticky routing',
          componentType: 'select',
          zodValidation: validations.shape.sticky_routing_options,
          inputProps: {
            options: [
              { value: 'Loosely bind', label: 'Loosely Bind' },
              { value: 'Strictly bind', label: 'Strictly Bind' },
              { value: 'No sticky routing', label: 'No Sticky Routing' },
            ],
          },
          className: 'mb-4',
        },
        {
          name: 'expiry',
          componentType: 'checkbox',
          label: 'No Expiry',
          zodValidation: validations.shape.expiry,
          onChangeListenTo: ['sticky_routing_options'],
          className: 'mb-4',
          getDynamicProps: (values) => ({
            inputProps: {
              disabled: values.sticky_routing_options === 'No sticky routing',
            },
          }),
        },
        {
          name: 'expiry_time_value',
          componentType: 'inputNumber',
          label: 'Expiry of sticky routing',
          zodValidation: validations.shape.expiry_time_value,
          onChangeListenTo: ['expiry', 'sticky_routing_options'],
          getDynamicProps: (values) => ({
            inputProps: {
              type: 'number',
              endIcon: <div className="text-p-400 mr-7 text-secondary">days</div>,
              disabled: values.expiry || values.sticky_routing_options === 'No sticky routing',
            },
          }),
        },
      ]}
      form={form}
    />
  )
}
